import { useState, useEffect } from 'react';
import Header from './components/Header';
import GameBoard from './components/GameBoard';
import Keyboard from './components/Keyboard';
import Settings from './components/Settings';
import Statistics from './components/Statistics';
import Footer from './components/Footer';
import { WORDS } from '../../mocks/words';
import { playCorrectSound, playWrongSound, playWinSound, playLoseSound, playKeyClickSound } from '../../hooks/useSoundEffects';

export interface GameStats {
  played: number;
  won: number;
  currentStreak: number;
  maxStreak: number;
  guessDistribution: number[];
}

export default function Home() {
  const [targetWord, setTargetWord] = useState('');
  const [guesses, setGuesses] = useState<string[]>([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'lost'>('playing');
  const [showSettings, setShowSettings] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [stats, setStats] = useState<GameStats>({
    played: 0,
    won: 0,
    currentStreak: 0,
    maxStreak: 0,
    guessDistribution: [0, 0, 0, 0, 0, 0]
  });

  useEffect(() => {
    const savedStats = localStorage.getItem('wordleStats');
    if (savedStats) {
      setStats(JSON.parse(savedStats));
    }
    const savedDarkMode = localStorage.getItem('wordleDarkMode');
    if (savedDarkMode) {
      setDarkMode(JSON.parse(savedDarkMode));
    }
    const savedSoundEnabled = localStorage.getItem('wordleSoundEnabled');
    if (savedSoundEnabled !== null) {
      setSoundEnabled(JSON.parse(savedSoundEnabled));
    }
    startNewGame();
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const startNewGame = () => {
    const randomWord = WORDS[Math.floor(Math.random() * WORDS.length)].toUpperCase();
    setTargetWord(randomWord);
    setGuesses([]);
    setCurrentGuess('');
    setGameStatus('playing');
  };

  const handleKeyPress = (key: string) => {
    if (gameStatus !== 'playing') return;

    if (key === 'ENTER') {
      if (currentGuess.length !== 5) return;
      
      const newGuesses = [...guesses, currentGuess];
      setGuesses(newGuesses);

      if (currentGuess === targetWord) {
        setGameStatus('won');
        updateStats(true, newGuesses.length);
        if (soundEnabled) {
          playCorrectSound();
          setTimeout(() => playWinSound(), 600);
        }
        setTimeout(() => setShowStats(true), 1500);
      } else if (newGuesses.length === 6) {
        setGameStatus('lost');
        updateStats(false, 0);
        if (soundEnabled) {
          playLoseSound();
        }
        setTimeout(() => setShowStats(true), 1500);
      } else {
        if (soundEnabled) {
          playWrongSound();
        }
      }

      setCurrentGuess('');
    } else if (key === 'BACKSPACE') {
      setCurrentGuess(currentGuess.slice(0, -1));
      if (soundEnabled) {
        playKeyClickSound();
      }
    } else if (currentGuess.length < 5 && /^[A-Z]$/.test(key)) {
      setCurrentGuess(currentGuess + key);
      if (soundEnabled) {
        playKeyClickSound();
      }
    }
  };

  const updateStats = (won: boolean, attempts: number) => {
    const newStats = { ...stats };
    newStats.played += 1;
    
    if (won) {
      newStats.won += 1;
      newStats.currentStreak += 1;
      newStats.maxStreak = Math.max(newStats.maxStreak, newStats.currentStreak);
      newStats.guessDistribution[attempts - 1] += 1;
    } else {
      newStats.currentStreak = 0;
    }

    setStats(newStats);
    localStorage.setItem('wordleStats', JSON.stringify(newStats));
  };

  const handleNewGame = () => {
    startNewGame();
    setShowStats(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showSettings || showStats) return;
      
      if (e.key === 'Enter') {
        handleKeyPress('ENTER');
      } else if (e.key === 'Backspace') {
        handleKeyPress('BACKSPACE');
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        handleKeyPress(e.key.toUpperCase());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentGuess, guesses, gameStatus, showSettings, showStats]);

  const getLetterStates = () => {
    const states: Record<string, 'correct' | 'present' | 'absent'> = {};
    
    guesses.forEach(guess => {
      guess.split('').forEach((letter, index) => {
        if (targetWord[index] === letter) {
          states[letter] = 'correct';
        } else if (targetWord.includes(letter) && states[letter] !== 'correct') {
          states[letter] = 'present';
        } else if (!states[letter]) {
          states[letter] = 'absent';
        }
      });
    });

    return states;
  };

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'dark bg-gray-900' : 'bg-white'}`}>
      <Header 
        onSettingsClick={() => setShowSettings(true)}
        onStatsClick={() => setShowStats(true)}
        darkMode={darkMode}
      />
      
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <GameBoard 
          guesses={guesses}
          currentGuess={currentGuess}
          targetWord={targetWord}
          gameStatus={gameStatus}
          darkMode={darkMode}
        />
        
        <div className="mt-8 w-full max-w-lg">
          <Keyboard 
            onKeyPress={handleKeyPress}
            letterStates={getLetterStates()}
            darkMode={darkMode}
          />
        </div>

        {gameStatus !== 'playing' && (
          <div className="mt-6">
            <button
              onClick={handleNewGame}
              className="px-8 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
            >
              New Game
            </button>
          </div>
        )}
      </main>

      <Footer darkMode={darkMode} />

      {showSettings && (
        <Settings
          onClose={() => setShowSettings(false)}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          soundEnabled={soundEnabled}
          setSoundEnabled={setSoundEnabled}
        />
      )}

      {showStats && (
        <Statistics
          onClose={() => setShowStats(false)}
          stats={stats}
          gameStatus={gameStatus}
          targetWord={targetWord}
          onNewGame={handleNewGame}
          darkMode={darkMode}
        />
      )}
    </div>
  );
}
